import React, { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Card,
  Form,
  Select,
  InputNumber,
  Button,
  Typography,
  Spin,
  message,
  Result,
  Radio,
  Tooltip
} from 'antd'
import { UserOutlined, ReloadOutlined, SaveOutlined, InfoCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'

const { Title, Paragraph, Text } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 32px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ContentWrapper = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledCard = styled(Card)`
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(213, 114, 138, 0.15);
  border: 1px solid rgba(213, 114, 138, 0.12);

  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  span {
    color: #8c8c8c;
    font-size: 14px;
  }
`

const FormWrapper = styled(Form)`
  margin-top: 16px;

  .ant-form-item {
    margin-bottom: 24px;
  }
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

const EmployeeOptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const InfoCard = styled(Card)`
  border-radius: 16px;
  background: #ffffffcc;
  border: 1px dashed #f0ccd4;
`

const EmployeeSalarySettingsPage = () => {
  const { user, businessPlan } = useAuth()
  const navigate = useNavigate()
  const { hasFeature, permissionsLoaded } = useMembership()
  const [form] = Form.useForm()
  const [employees, setEmployees] = useState([])
  const [loadingEmployees, setLoadingEmployees] = useState(false)
  const [fetchingSalary, setFetchingSalary] = useState(false)
  const [saving, setSaving] = useState(false)

  const businessId = user?.businessId

  const fetchEmployees = useCallback(async () => {
    if (!businessId) return
    try {
      setLoadingEmployees(true)
      const response = await api.get(`/api/employees/${businessId}`)
      const employeesData = Array.isArray(response.data) ? response.data : []
      setEmployees(employeesData)
    } catch (error) {
      console.error('取得員工清單失敗', error)
      message.error('無法載入員工清單，請稍後再試')
    } finally {
      setLoadingEmployees(false)
    }
  }, [businessId])

  const fetchEmployeeSalary = useCallback(
    async (employeeId) => {
      if (!businessId || !employeeId) return
      try {
        setFetchingSalary(true)
        const response = await api.get(
          `/api/businesses/${businessId}/employees/${employeeId}/salary`
        )
        const salaryPayload =
          response.data?.data ?? response.data?.salary ?? response.data ?? {}
        const salaryValue =
          typeof salaryPayload === 'number'
            ? salaryPayload
            : salaryPayload.salary ?? salaryPayload.amount ?? 0
        const guaranteedSalaryValue = salaryPayload.guaranteedSalary ?? salaryPayload.guaranteed ?? salaryValue
        const salaryModeValue = salaryPayload.salaryMode || 'guaranteed_or_commission'
        
        form.setFieldsValue({ 
          salary: Number(salaryValue) || 0,
          guaranteedSalary: Number(guaranteedSalaryValue) || Number(salaryValue) || 0,
          salaryMode: salaryModeValue
        })
      } catch (error) {
        console.error('取得薪資資料失敗', error)
        form.setFieldsValue({ salary: 0 })
      } finally {
        setFetchingSalary(false)
      }
    },
    [businessId, form]
  )

  useEffect(() => {
    if (!hasFeature('salary')) return
    fetchEmployees()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, hasFeature])

  // 所有 Hooks 必須在權限檢查之前
  const employeeOptions = useMemo(() => {
    return employees.map((employee) => {
      const displayName = employee.name || employee.username || '未命名員工'
      const roleText = employee.businessRole
        ? `（${employee.businessRole}）`
        : ''
      const searchText = [
        displayName,
        employee.username,
        employee.email,
        employee.phone,
        employee.businessRole
      ]
        .filter(Boolean)
        .join(' ')

      return {
        value: employee._id,
        node: (
          <EmployeeOptionLabel>
            <UserOutlined style={{ color: '#d5728a' }} />
            <span>
              {displayName}
              {roleText}
            </span>
          </EmployeeOptionLabel>
        ),
        searchText
      }
    })
  }, [employees])

  const handleEmployeeChange = useCallback(
    (employeeId) => {
      form.setFieldsValue({ 
        salary: undefined,
        guaranteedSalary: undefined,
        salaryMode: 'guaranteed_or_commission'
      })
      if (employeeId) {
        fetchEmployeeSalary(employeeId)
      }
    },
    [fetchEmployeeSalary, form]
  )

  // 權限檢查：只有品牌方案才能使用此功能
  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageWrapper>
    )
  }

  if (!hasFeature('salary')) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要品牌方案才能使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </ContentWrapper>
      </PageWrapper>
    )
  }

  const handleReset = () => {
    form.resetFields()
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (!businessId) {
        message.error('未能取得商家資訊，請重新登入後再試')
        return
      }
      setSaving(true)
      const payload = {
        salary: Number(values.salary) || 0,
        salaryMode: values.salaryMode || 'guaranteed_or_commission'
      }
      
      // 如果是保底制度，需要包含保底金額
      if (values.salaryMode === 'guaranteed_or_commission') {
        payload.guaranteedSalary = Number(values.guaranteedSalary) || Number(values.salary) || 0
      }
      
      await api.put(
        `/api/businesses/${businessId}/employees/${values.employeeId}/salary`,
        payload
      )
      message.success('薪資設定已更新')
    } catch (error) {
      if (error?.errorFields) {
        return
      }
      console.error('更新薪資失敗', error)
      message.error(
        error.response?.data?.message ||
          error.message ||
          '更新薪資失敗，請稍後再試'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <StyledCard
          title={
            <Header>
              <Title level={3} style={{ marginBottom: 0, color: '#0f2562' }}>
                員工薪資設定
              </Title>
              <span>設定固定薪資，方便後續績效統計與薪資結算。</span>
            </Header>
          }
        >
          <Paragraph style={{ color: '#576079', marginBottom: 24 }}>
            選擇要設定的員工並輸入固定薪資金額。薪資下限為
            NT$0，請依照公司政策調整。
          </Paragraph>

          <FormWrapper
            layout='vertical'
            form={form}
            initialValues={{
              employeeId: undefined,
              salary: undefined,
              guaranteedSalary: undefined,
              salaryMode: 'guaranteed_or_commission'
            }}
          >
            <Form.Item
              name='employeeId'
              label='選擇員工'
              rules={[{ required: true, message: '請選擇員工' }]}
            >
              <Select
                placeholder='請選擇員工'
                size='large'
                loading={loadingEmployees}
                onChange={handleEmployeeChange}
                showSearch
                optionFilterProp='label'
              >
                {employeeOptions.map((option) => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={option.searchText}
                  >
                    {option.node}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name='salaryMode'
              label={
                <span>
                  薪資計算模式
                  <Tooltip title='選擇員工的薪資計算方式。保底制度：未達業績門檻時使用保底金額，達標後改為底薪+抽成。底薪+抽成：不論業績高低，一律底薪+當月總抽成。'>
                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
                  </Tooltip>
                </span>
              }
              rules={[{ required: true, message: '請選擇薪資計算模式' }]}
            >
              <Radio.Group>
                <Radio value='guaranteed_or_commission'>
                  <div>
                    <div style={{ fontWeight: 500 }}>保底制度</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      未達業績門檻：保底金額與抽成取高者
                      <br />
                      達標後：底薪和總抽成取高者
                    </div>
                  </div>
                </Radio>
                <Radio value='base_plus_commission' style={{ marginTop: 12 }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>底薪+抽成</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      不論業績高低，一律：底薪 + 當月總抽成
                    </div>
                  </div>
                </Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name='salary'
              label='底薪'
              rules={[
                { required: true, message: '請輸入底薪' },
                {
                  type: 'number',
                  min: 0,
                  message: '底薪不能小於 0'
                }
              ]}
            >
              <InputNumber
                size='large'
                min={0}
                style={{ width: '100%' }}
                placeholder='輸入底薪'
                formatter={(value) => {
                  if (value === undefined || value === null) return ''
                  const numeric = Number(value)
                  if (Number.isNaN(numeric)) return ''
                  return `NT$ ${numeric
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                }}
                parser={(value) =>
                  value ? Number(value.replace(/[^\d.]/g, '')) : 0
                }
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.salaryMode !== currentValues.salaryMode
              }
            >
              {({ getFieldValue }) => {
                const salaryMode = getFieldValue('salaryMode')
                if (salaryMode === 'guaranteed_or_commission') {
                  return (
                    <Form.Item
                      name='guaranteedSalary'
                      label='保底金額'
                      rules={[
                        { required: true, message: '請輸入保底金額' },
                        {
                          type: 'number',
                          min: 0,
                          message: '保底金額不能小於 0'
                        }
                      ]}
                    >
                      <InputNumber
                        size='large'
                        min={0}
                        style={{ width: '100%' }}
                        placeholder='輸入保底金額（未達標時使用）'
                        formatter={(value) => {
                          if (value === undefined || value === null) return ''
                          const numeric = Number(value)
                          if (Number.isNaN(numeric)) return ''
                          return `NT$ ${numeric
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                        }}
                        parser={(value) =>
                          value ? Number(value.replace(/[^\d.]/g, '')) : 0
                        }
                      />
                    </Form.Item>
                  )
                }
                return null
              }}
            </Form.Item>

            <Actions>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                清除
              </Button>
              <Button
                type='primary'
                icon={<SaveOutlined />}
                onClick={handleSubmit}
                loading={saving}
                disabled={fetchingSalary || loadingEmployees}
              >
                保存設定
              </Button>
            </Actions>
          </FormWrapper>

          {fetchingSalary && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <Spin size='small' tip='載入薪資資料中...' />
            </div>
          )}
        </StyledCard>

        <InfoCard>
          <Title level={5} style={{ color: '#0f2562', marginBottom: 12 }}>
            小提醒
          </Title>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>• 為避免計算誤差，建議統一以月薪或固定時薪設定。</Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>
              • 若員工薪資需要特殊設定，可於備註或其他系統同步更新。
            </Text>
          </Paragraph>
          <Paragraph>
            <Text strong>• 薪資設定僅限具有管理權限的使用者操作。</Text>
          </Paragraph>
        </InfoCard>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default EmployeeSalarySettingsPage
